"use client";

import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp, RotateCcw, Sparkles, X } from "lucide-react";
import axios from "axios";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STARTER_MESSAGE = {
  role: "assistant",
  content: "Hi! I’m Guru, your GuruTech AI assistant. Ask me about our AI services, process, or how we can help your business.",
};

export function ColorOrb({ dimension = "24px", className }) {
  return (
    <span
      aria-hidden="true"
      className={cn("guru-orb inline-block shrink-0 rounded-full", className)}
      style={{ width: dimension, height: dimension }}
    />
  );
}

export function MorphPanel() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [messages, setMessages] = React.useState([STARTER_MESSAGE]);
  const [input, setInput] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const inputRef = React.useRef(null);
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 250);
  }, [isOpen]);

  React.useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, isLoading]);

  const resetChat = () => {
    setMessages([STARTER_MESSAGE]);
    setInput("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const question = input.trim();
    if (!question || isLoading) return;

    const nextMessages = [...messages, { role: "user", content: question }];
    setMessages(nextMessages);
    setInput("");
    setIsLoading(true);

    try {
      const history = nextMessages.filter((message) => message !== STARTER_MESSAGE).slice(-12);
      const { data } = await axios.post(`${API}/chat`, { messages: history });
      setMessages((current) => [...current, { role: "assistant", content: data.answer }]);
    } catch (error) {
      const detail = error.response?.data?.detail;
      setMessages((current) => [
        ...current,
        { role: "assistant", content: detail || "I’m having trouble connecting right now. Please try again shortly." },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed bottom-5 right-4 z-[100] sm:bottom-7 sm:right-7" data-testid="ai-chatbot">
      <AnimatePresence mode="wait">
        {isOpen ? (
          <motion.section
            key="open"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 420, damping: 34 }}
            className="flex h-[min(600px,calc(100vh-2.5rem))] w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-3xl border border-white/15 bg-[#090b12]/95 shadow-[0_24px_90px_rgba(0,0,0,.6),0_0_50px_rgba(124,58,237,.18)] backdrop-blur-2xl sm:w-[390px]"
            aria-label="GuruTech AI assistant"
          >
            <header className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
              <ColorOrb dimension="38px" />
              <div className="min-w-0 flex-1">
                <h2 className="font-unbounded text-sm font-medium text-white">Ask Guru</h2>
                <p className="mt-0.5 flex items-center gap-1.5 text-xs text-white/50">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> GuruTech AI assistant
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={resetChat} aria-label="Start a new chat" className="rounded-full text-white/50 hover:bg-white/10 hover:text-white">
                <RotateCcw />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)} aria-label="Close chat" className="rounded-full text-white/50 hover:bg-white/10 hover:text-white">
                <X />
              </Button>
            </header>

            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-5" aria-live="polite">
              {messages.map((message, index) => (
                <div key={`${message.role}-${index}`} className={cn("flex", message.role === "user" ? "justify-end" : "justify-start")}>
                  <div className={cn("max-w-[86%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed", message.role === "user" ? "rounded-br-md bg-electric-violet text-white" : "rounded-bl-md border border-white/10 bg-white/[0.06] text-white/80")}>
                    {message.content}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-md border border-white/10 bg-white/[0.06] px-4 py-3 w-fit" aria-label="Guru is thinking">
                  {[0, 1, 2].map((dot) => <motion.span key={dot} className="h-1.5 w-1.5 rounded-full bg-white/50" animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }} transition={{ duration: 0.9, repeat: Infinity, delay: dot * 0.15 }} />)}
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="border-t border-white/10 p-3">
              <div className="flex items-end gap-2 rounded-2xl border border-white/10 bg-white/[0.05] p-2 focus-within:border-electric-violet/70">
                <textarea ref={inputRef} value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); event.currentTarget.form?.requestSubmit(); } }} rows={1} maxLength={2000} placeholder="Ask about our AI services…" className="max-h-28 min-h-10 flex-1 resize-none bg-transparent px-2 py-2 text-sm text-white outline-none placeholder:text-white/35" />
                <Button type="submit" size="icon" disabled={!input.trim() || isLoading} aria-label="Send message" className="shrink-0 rounded-xl bg-electric-violet text-white hover:bg-electric-violet/90"><ArrowUp /></Button>
              </div>
              <p className="mt-2 text-center text-[10px] text-white/30">Answers are limited to GuruTech AI and its services.</p>
            </form>
          </motion.section>
        ) : (
          <motion.button key="closed" initial={{ opacity: 0, y: 12, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} onClick={() => setIsOpen(true)} className="group flex h-14 items-center gap-3 rounded-full border border-white/15 bg-[#0b0d14]/95 px-3 pr-5 text-white shadow-[0_15px_50px_rgba(0,0,0,.5),0_0_35px_rgba(124,58,237,.22)] backdrop-blur-xl" aria-label="Open GuruTech AI assistant">
            <ColorOrb dimension="34px" />
            <span className="font-unbounded text-sm font-medium">Ask Guru</span>
            <Sparkles className="h-4 w-4 text-neon-cyan transition-transform group-hover:rotate-12" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

export default MorphPanel;
